import React, {createContext, useContext, useMemo} from 'react';
import PropTypes from 'prop-types';
import {
  BiUserCircle,
  BiCog,
  BiUserCheck,
  BiHistory,
  BiBarChartAlt2,
  BiSend,
  BiGroup,
  BiLayer,
  BiCreditCard,
  BiBuilding,
  BiHome,
  BiWallet,
  BiLineChart,
  BiCheckShield,
  BiReceipt,
  BiBook,
  BiListUl,
  BiBookmark,
  BiFullscreen,
  BiChat,
  BiBot,
  BiLink,
  BiServer,
  BiCalendar,
} from 'react-icons/bi';
import {FaWhatsapp} from 'react-icons/fa';
import {
  Contacts as ContactsIcon,
  Group as GroupIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';

const DynamicRoutesContext = createContext();

export const useDynamicRoutes = () => {
  const context = useContext(DynamicRoutesContext);
  if (!context) {
    throw new Error(
      'useDynamicRoutes must be used within a DynamicRoutesProvider',
    );
  }
  return context;
};

export const DynamicRoutesProvider = ({children}) => {
  // Role-based routes for the new v3.2.0 structure
  // Using 'auth' property for role-based access control (matching sidebar implementation)
  const routes = useMemo(
    () => [
      // ==================== SUPERADMIN ONLY ====================
      {
        id: 'platform',
        title: 'Platform',
        messageId: 'sidebar.platform',
        type: 'collapse',
        icon: <BiBuilding />,
        // admin is a platform manager and sees the platform tools it is
        // backend-authorized + keyless for; per-item auth below narrows it.
        auth: ['superadmin', 'admin'],
        children: [
          {
            id: 'platformOverview',
            title: 'Overview',
            messageId: 'sidebar.platformOverview',
            type: 'item',
            url: '/platform/overview',
            icon: <BiBarChartAlt2 />,
            // admin lands here too (role-aware element renders the Admin Console)
            auth: ['superadmin', 'admin'],
          },
          {
            id: 'platformUsers',
            title: 'All Users',
            messageId: 'sidebar.platformUsers',
            type: 'item',
            url: '/platform/users',
            icon: <BiUserCircle />,
            auth: ['superadmin'],
          },
          {
            id: 'platformSendOnBehalf',
            title: 'Send on Behalf',
            messageId: 'sidebar.platformSendOnBehalf',
            type: 'item',
            url: '/platform/send-on-behalf',
            icon: <BiSend />,
            auth: ['superadmin', 'admin'],
          },
          {
            id: 'platformSendThrottle',
            title: 'Send Throttle',
            messageId: 'sidebar.platformSendThrottle',
            type: 'item',
            url: '/platform/send-throttle',
            icon: <BiCheckShield />,
            auth: ['superadmin', 'admin'],
          },
          {
            id: 'platformThrottleMonitor',
            title: 'Throttle Monitor',
            messageId: 'sidebar.platformThrottleMonitor',
            type: 'item',
            url: '/platform/throttle-monitor',
            icon: <BiServer />,
            auth: ['superadmin', 'admin'],
          },
          {
            id: 'platformPlans',
            title: 'Plans & Pricing',
            messageId: 'sidebar.platformPlans',
            type: 'item',
            url: '/platform/plans',
            icon: <BiCreditCard />,
            auth: ['superadmin'],
          },
          {
            id: 'platformSmtpSettings',
            title: 'SMTP Settings',
            messageId: 'sidebar.platformSmtpSettings',
            type: 'item',
            url: '/platform/smtp-settings',
            icon: <BiCog />,
            auth: ['superadmin', 'admin'],
          },
          {
            id: 'platformBalanceMonitor',
            title: 'Balance Monitor',
            messageId: 'sidebar.platformBalanceMonitor',
            type: 'item',
            url: '/platform/balance-monitor',
            icon: <BiWallet />,
            auth: ['superadmin'],
          },
          {
            id: 'platformFinancial',
            title: 'Financial',
            messageId: 'sidebar.platformFinancial',
            type: 'item',
            url: '/platform/financial',
            icon: <BiLineChart />,
            auth: ['superadmin'],
          },
          {
            id: 'platformExpenses',
            title: 'Expenses',
            messageId: 'sidebar.platformExpenses',
            type: 'item',
            url: '/platform/expenses',
            icon: <BiReceipt />,
            auth: ['superadmin'],
          },
          {
            id: 'platformPendingApprovals',
            title: 'Pending Approvals',
            messageId: 'sidebar.platformPendingApprovals',
            type: 'item',
            url: '/platform/pending-approvals',
            icon: <BiCheckShield />,
            auth: ['superadmin'],
          },
          {
            id: 'platformMonitor',
            title: 'Platform Monitor',
            messageId: 'sidebar.platformMonitor',
            type: 'item',
            url: '/platform/monitor',
            icon: <BiFullscreen />,
            // proxyApi-backed (needs api_key) -> superadmin only; keyless admin 403s
            auth: ['superadmin'],
          },
          {
            id: 'platformSystemLogs',
            title: 'System Logs',
            messageId: 'sidebar.platformSystemLogs',
            type: 'item',
            url: '/platform/system-logs',
            icon: <BiListUl />,
            auth: ['superadmin'],
          },
        ],
      },

      // ==================== PROVIDER ONLY ====================
      {
        id: 'provider',
        title: 'Provider',
        messageId: 'sidebar.provider',
        type: 'collapse',
        icon: <BiLayer />,
        auth: ['provider'],
        permittedRole: ['provider'],
        children: [
          {
            id: 'providerOverview',
            title: 'My Account',
            messageId: 'sidebar.providerOverview',
            type: 'item',
            url: '/provider/overview',
            icon: <BiHome />,
            auth: ['provider'],
          },
          {
            id: 'providerUserOverview',
            title: 'My Overview',
            messageId: 'sidebar.userDashboard',
            type: 'item',
            url: '/user/overview',
            icon: <BiBarChartAlt2 />,
            auth: ['provider'],
          },
          {
            id: 'providerServiceUsers',
            title: 'Service Users',
            messageId: 'sidebar.providerServiceUsers',
            type: 'item',
            url: '/provider/service-users',
            icon: <BiUserCircle />,
            auth: ['provider'],
          },
        ],
      },

      // ==================== USER DASHBOARD ====================
      {
        id: 'userDashboard',
        title: 'Dashboard',
        messageId: 'sidebar.userDashboard',
        type: 'item',
        url: '/user/overview',
        icon: <BiBarChartAlt2 />,
        auth: ['user'],
      },

      // ==================== SHARED: WHATSAPP MESSAGING ====================
      {
        id: 'muraasala',
        title: 'WhatsApp Messaging',
        messageId: 'sidebar.muraasala',
        type: 'collapse',
        icon: <FaWhatsapp />,
        auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
        children: [
          {
            id: 'muraasalaConnection',
            title: 'Connection',
            messageId: 'sidebar.muraasalaConnection',
            type: 'item',
            url: '/service/connection',
            icon: <BiCog />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.connection',
          },
          {
            id: 'muraasalaSend',
            title: 'Send Message',
            messageId: 'sidebar.muraasalaSend',
            type: 'item',
            url: '/service/send',
            icon: <BiSend />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.send',
          },
          {
            id: 'muraasalaConversations',
            title: 'Conversations',
            messageId: 'sidebar.muraasalaConversations',
            type: 'item',
            url: '/service/conversations',
            icon: <BiChat />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.conversations',
          },
          {
            id: 'muraasalaWebhooks',
            title: 'Webhooks',
            messageId: 'sidebar.muraasalaWebhooks',
            type: 'item',
            url: '/service/webhooks',
            icon: <BiLink />,
            // No portalPageKey: the API is per-user, so this is owner-scoped.
            auth: ['superadmin', 'admin', 'provider', 'user'],
          },
          {
            id: 'muraasalaAutoReply',
            title: 'Auto-Reply Rules',
            messageId: 'sidebar.muraasalaAutoReply',
            type: 'item',
            url: '/service/auto-reply',
            icon: <BiBot />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.autoReply',
          },
          {
            id: 'muraasalaBulk',
            title: 'Bulk Messages',
            messageId: 'sidebar.muraasalaBulk',
            type: 'item',
            url: '/service/bulk',
            icon: <BiGroup />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.bulk',
          },
          {
            id: 'muraasalaHistory',
            title: 'Message History',
            messageId: 'sidebar.muraasalaHistory',
            type: 'item',
            url: '/service/history',
            icon: <BiHistory />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.history',
          },
          {
            id: 'muraasalaReports',
            title: 'Reports & Analytics',
            messageId: 'sidebar.muraasalaReports',
            type: 'item',
            url: '/service/reports',
            icon: <BiBarChartAlt2 />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.reports',
          },
          {
            id: 'muraasalaMonitor',
            title: 'System Monitor',
            messageId: 'sidebar.muraasalaMonitor',
            type: 'item',
            url: '/service/monitor',
            icon: <BiServer />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.monitor',
          },
          {
            id: 'muraasalaContacts',
            title: 'Contacts',
            messageId: 'sidebar.serviceContacts',
            type: 'item',
            url: '/service/contacts',
            exact: true,
            icon: <ContactsIcon />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.contacts',
          },
          {
            id: 'muraasalaContactGroups',
            title: 'Contact Groups',
            messageId: 'sidebar.serviceContactGroups',
            type: 'item',
            url: '/service/contact-groups',
            icon: <GroupIcon />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.contacts',
          },
          {
            id: 'muraasalaContactsImport',
            title: 'Import Contacts',
            messageId: 'sidebar.serviceContactsImport',
            type: 'item',
            url: '/service/contacts/import',
            icon: <FileUploadIcon />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.contacts',
          },
          {
            id: 'muraasalaScheduledCampaigns',
            title: 'Scheduled Campaigns',
            messageId: 'sidebar.serviceScheduledCampaigns',
            type: 'item',
            url: '/service/scheduled-campaigns',
            icon: <BiCalendar />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.send',
          },
          {
            id: 'muraasalaPolls',
            title: 'Polls',
            messageId: 'sidebar.servicePolls',
            type: 'item',
            url: '/service/polls',
            icon: <BiCalendar />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            // service.bulk, not service.send: a poll to many people IS a bulk
            // send and is gated as one on the API.
            portalPageKey: 'service.bulk',
          },
          {
            id: 'muraasalaWhatsAppGroups',
            title: 'WhatsApp Groups',
            messageId: 'sidebar.serviceGroups',
            type: 'item',
            url: '/service/groups',
            icon: <FaWhatsapp />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.contacts',
          },
          {
            id: 'muraasalaAnalytics',
            title: 'Analytics',
            messageId: 'sidebar.serviceAnalytics',
            type: 'item',
            url: '/service/analytics',
            icon: <BiBarChartAlt2 />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.analytics',
          },
          {
            id: 'muraasalaGroupMessages',
            title: 'Group Messages',
            messageId: 'sidebar.serviceGroupMessages',
            type: 'item',
            url: '/service/group-messages',
            icon: <BiGroup />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.send',
          },
          {
            id: 'muraasalaTemplates',
            title: 'Message Templates',
            messageId: 'sidebar.serviceTemplates',
            type: 'item',
            url: '/service/templates',
            icon: <BiBookmark />,
            auth: ['superadmin', 'admin', 'provider', 'user', 'portal_user'],
            portalPageKey: 'service.templates',
          },
        ],
      },

      // ==================== SHARED: PORTAL USERS ====================
      {
        id: 'portalUsers',
        title: 'Portal Users',
        messageId: 'sidebar.portalUsers',
        type: 'item',
        url: '/portal-users',
        icon: <BiUserCheck />,
        // Portal-user management is owned by a keyed parent (proxyApi requires an
        // api_key). The keyless admin role owns none and 403s -> exclude admin.
        auth: ['superadmin', 'provider', 'user'],
      },

      // ==================== GUIDES ====================
      {
        id: 'userGuide',
        title: 'User Guide',
        messageId: 'sidebar.userGuide',
        type: 'item',
        url: '/service/guide',
        icon: <BiBook />,
        auth: ['user', 'portal_user'],
      },
      {
        id: 'providerGuide',
        title: 'Provider Guide',
        messageId: 'sidebar.providerGuide',
        type: 'item',
        url: '/provider/guide',
        icon: <BiBook />,
        auth: ['provider', 'superadmin'],
      },

      // ==================== ACCOUNT & SETTINGS ====================
      {
        id: 'account',
        title: 'My Account',
        messageId: 'sidebar.account',
        type: 'collapse',
        icon: <BiUserCheck />,
        auth: ['superadmin', 'admin', 'provider', 'user'],
        children: [
          {
            id: 'profile',
            title: 'Profile',
            messageId: 'sidebar.profile',
            type: 'item',
            url: '/account/profile',
            icon: <BiUserCircle />,
            auth: ['superadmin', 'admin', 'provider', 'user'],
          },
          {
            id: 'settings',
            title: 'Settings',
            messageId: 'sidebar.settings',
            type: 'item',
            url: '/account/settings',
            icon: <BiCog />,
            auth: ['superadmin', 'admin', 'provider', 'user'],
          },
        ],
      },
    ],
    [],
  );

  return (
    <DynamicRoutesContext.Provider value={{routes, loading: false}}>
      {children}
    </DynamicRoutesContext.Provider>
  );
};

DynamicRoutesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
